import { Component } from '@theme/component';

/**
 * @typedef {Object} TabsRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} [strip]
 */

/**
 * Converts a string to a URL-safe slug, handling diacritics (including Czech).
 * @param {string} str
 * @returns {string}
 */
function slugify(str) {
  return str
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Builds unique slugs for all tabs, handling duplicates and empty labels.
 * @param {HTMLElement[]} tabs
 * @returns {string[]}
 */
function buildSlugs(tabs) {
  /** @type {Set<string>} */
  const taken = new Set();
  return tabs.map((tab, i) => {
    const label = tab.dataset.label?.trim() ?? '';
    const base = (label ? slugify(label) : '') || `tab-${i + 1}`;

    let slug = base;
    let suffix = 2;
    while (taken.has(slug)) {
      slug = `${base}-${suffix}`;
      suffix += 1;
    }
    taken.add(slug);
    return slug;
  });
}

/** @extends {Component<TabsRefs>} */
export class TabsComponent extends Component {
  #active = 0;
  /** @type {string[]} */
  #slugs = [];
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#hoistTabsToStrip();
    this.#slugs = buildSlugs(tabs);

    const hashIndex = this.#findHashIndex();
    this.#active = hashIndex >= 0 ? hashIndex : 0;

    this.#updateActiveTab(true);
    this.#setupEventListeners();
  }

  updatedCallback() {
    super.updatedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#hoistTabsToStrip();
    this.#slugs = buildSlugs(tabs);
  }

  /**
   * Tabs are emitted by `_mx-tab.liquid` as siblings of their panels so the
   * block has proper context for `block.settings.heading` (theme blocks don't
   * expose settings through `section.blocks` iteration). We relocate them into
   * the `.mx-tabs__strip` wrapper here so the strip can be its own
   * horizontally-scrollable flex container.
   */
  #hoistTabsToStrip() {
    const strip = this.refs.strip;
    if (!strip) return;
    for (const tab of this.refs.tabs ?? []) {
      if (tab.parentElement !== strip) strip.appendChild(tab);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * Public method to select a tab by index (used by theme editor).
   * @param {number} index
   * @param {{ instant?: boolean }} [options]
   */
  select(index, options) {
    this.#activate(index, options?.instant === true);
  }

  #findHashIndex() {
    const hash = location.hash.slice(1);
    if (!hash) return -1;
    return this.#slugs.indexOf(hash);
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', () => this.#activate(i), opts);
    }

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);
  }

  /** @param {KeyboardEvent} e */
  #handleKeydown(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs) return;

    const i = tabs.indexOf(target);

    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -i,
      End: tabs.length - 1 - i,
    };

    const offset = navMap[e.key];
    if (offset !== undefined) {
      e.preventDefault();
      const nextIndex = Math.min(Math.max(i + offset, 0), tabs.length - 1);
      tabs[nextIndex]?.focus();
      this.#activate(nextIndex);
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.#activate(i);
    }
  }

  /**
   * @param {number} index
   * @param {boolean} [instant]
   */
  #activate(index, instant = false) {
    const { tabs } = this.refs;
    if (!tabs || index < 0 || index >= tabs.length) return;

    this.#active = index;
    this.#updateActiveTab(instant);

    const slug = this.#slugs[index];
    if (slug) {
      history.replaceState(null, '', `#${slug}`);
    }
  }

  /** @param {boolean} instant */
  #updateActiveTab(instant) {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of (tabs ?? []).entries()) {
      const isActive = i === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('mx-tabs__tab--active', isActive);
    }

    for (const [i, panel] of (panels ?? []).entries()) {
      const isActive = i === this.#active;
      panel.toggleAttribute('hidden', !isActive);
    }

    // Scroll only the strip's internal horizontal scrollbar to centre the
    // active tab. Using scrollIntoView here propagates to the document scroll
    // during hydration (before layout is settled) and causes an unwanted
    // ~360 px page jump on load.
    const activeTab = tabs?.[this.#active];
    const strip = this.refs.strip;
    if (activeTab && strip) {
      const tabRect = activeTab.getBoundingClientRect();
      const stripRect = strip.getBoundingClientRect();
      const target = strip.scrollLeft + tabRect.left - stripRect.left - (stripRect.width - tabRect.width) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: instant ? 'instant' : 'smooth' });
    }
  }
}

if (!customElements.get('mx-tabs-component')) {
  customElements.define('mx-tabs-component', TabsComponent);
}
